import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import {BiCrown } from 'react-icons/bi';
import { db } from '../config/firebaseConfig';

/**
 * A badge component to display upgrade prompt for non-subscribers
 */
const SubscriptionBadge = () => {
  const { user } = useAuth();
  const [isSubscriptionActive, setIsSubscriptionActive] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const checkSubscriptionStatus = async () => {
      if (!user) {
        setIsSubscriptionActive(false);
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        let hasActiveSubscription = false;

        // Check method 1: Users collection
        if (user.email) {
          const userDocRef = doc(db, 'users', user.email);
          const userDoc = await getDoc(userDocRef);
          
          if (userDoc.exists()) {
            const userData = userDoc.data();
            
            // Check if subscription is active
            if (userData.subscriptionStatus === 'active') {
              // Check if not expired
              if (userData.expiryDate) {
                const expiryDate = userData.expiryDate instanceof Date 
                  ? userData.expiryDate 
                  : new Date(userData.expiryDate.seconds * 1000);
                
                if (expiryDate > new Date()) {
                  hasActiveSubscription = true;
                }
              } else {
                // No expiry date but active status
                hasActiveSubscription = true;
              }
            }
          }
        }

        // Check method 2: Purchases collection
        if (!hasActiveSubscription && user.uid) {
          const purchaseRef = doc(db, 'purchases', user.uid);
          const purchaseDoc = await getDoc(purchaseRef);
          
          if (purchaseDoc.exists()) {
            const purchaseData = purchaseDoc.data();
            
            if (purchaseData.status === 'active') {
              // Check expiry date
              if (purchaseData.expiryDate) {
                const expiryDate = purchaseData.expiryDate instanceof Date 
                  ? purchaseData.expiryDate
                  : new Date(purchaseData.expiryDate.seconds * 1000);
                
                if (expiryDate > new Date()) {
                  hasActiveSubscription = true;
                }
              } else {
                // No expiry date but active status
                hasActiveSubscription = true;
              }
            }
          }
        }
        
        // Final check: Query purchases by email
        if (!hasActiveSubscription && user.email) {
          const purchasesRef = collection(db, 'purchases');
          const q = query(purchasesRef, where('email', '==', user.email));
          const querySnapshot = await getDocs(q);
          
          querySnapshot.forEach(doc => {
            const purchaseData = doc.data();
            
            if (purchaseData.status === 'active') {
              if (purchaseData.expiryDate) {
                const expiryDate = purchaseData.expiryDate instanceof Date
                  ? purchaseData.expiryDate
                  : new Date(purchaseData.expiryDate.seconds * 1000);
                  
                if (expiryDate > new Date()) {
                  hasActiveSubscription = true;
                }
              } else {
                hasActiveSubscription = true;
              }
            }
          });
        }

        setIsSubscriptionActive(hasActiveSubscription);
      } catch (error) {
        console.error('Error checking subscription status:', error);
      } finally {
        setIsLoading(false);
      }
    };

    checkSubscriptionStatus();
  }, [user]);

  // Don't show anything while loading
  if (isLoading) {
    return null;
  }

  // Only show the upgrade badge if user is logged in and doesn't have an active subscription
  if (user && !isSubscriptionActive) {
    return (
      <div className="upgrade-badge text-center px-3 py-2 mt-2 mb-4 mx-4 rounded-md bg-amber-600 text-white hover:bg-amber-700 transition-colors">
        <Link to="/plans" className="block w-full h-full uppercase">
          <div className="font-medium text-sm flex"><BiCrown className='mr-2' size={18}/> Upgrade Now</div> 
        </Link>
      </div>
    );
  }
  
  // Don't show anything for subscribed users or not logged in users
  return null;
};

export default SubscriptionBadge;